import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Briefcase,
  Plus,
  Search,
  Building2,
  DollarSign,
  Phone,
  Mail,
  X,
  Edit,
  Trash2,
  MessageSquare,
  GripVertical,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const WCANetwork = () => {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDealDetail, setShowDealDetail] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [newDeal, setNewDeal] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    stage: "diagnostico",
    value: 0,
    description: "",
    active_routes: [],
  });
  const [newNote, setNewNote] = useState("");

  const stages = [
    { id: "diagnostico", label: "Diagnóstico", color: "#6C8ECC" },
    { id: "activacion", label: "Activación", color: "#9EC6EE" },
    { id: "ruta", label: "Ruta", color: "#F59E0B" },
    { id: "cierre", label: "Cierre", color: "#10B981" },
  ];

  const transformationRoutes = [
    "Transformación Digital",
    "Liderazgo TechHuman",
    "Cultura de Innovación",
    "Experiencia del Cliente",
    "Sostenibilidad",
  ];

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/deals`, { withCredentials: true });
      setDeals(response.data);
    } catch (error) {
      console.error("Error fetching deals:", error);
      // Initialize with empty array for production
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const newDeals = [...deals];
    const dealIndex = newDeals.findIndex((d) => d.deal_id === draggableId);
    if (dealIndex > -1) {
      newDeals[dealIndex] = {
        ...newDeals[dealIndex],
        stage: destination.droppableId,
      };
      setDeals(newDeals);

      try {
        await axios.put(
          `${API}/deals/${draggableId}`,
          { stage: destination.droppableId },
          { withCredentials: true }
        );
      } catch (error) {
        console.error("Error updating deal:", error);
      }
    }
  };

  const handleCreateDeal = async () => {
    if (!newDeal.company_name.trim()) return;
    
    try {
      const response = await axios.post(`${API}/deals`, newDeal, {
        withCredentials: true,
      });
      setDeals([...deals, response.data]);
    } catch (error) {
      console.error("Error creating deal:", error);
      // Add locally for demo
      const mockDeal = {
        deal_id: `deal_${Date.now()}`,
        ...newDeal,
        notes: [],
        created_at: new Date().toISOString(),
      };
      setDeals([...deals, mockDeal]);
    }
    
    setShowModal(false);
    setEditingDeal(null);
    setNewDeal({
      company_name: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      stage: "diagnostico",
      value: 0,
      description: "",
      active_routes: [],
    });
  };

  const handleEditDeal = (deal) => {
    setEditingDeal(deal);
    setNewDeal({
      company_name: deal.company_name,
      contact_name: deal.contact_name,
      contact_email: deal.contact_email,
      contact_phone: deal.contact_phone || "",
      stage: deal.stage,
      value: deal.value,
      description: deal.description || "",
      active_routes: deal.active_routes || [],
    });
    setShowModal(true);
  };

  const handleUpdateDeal = async () => {
    if (!editingDeal) return;
    
    try {
      await axios.put(`${API}/deals/${editingDeal.deal_id}`, newDeal, {
        withCredentials: true,
      });
    } catch (error) {
      console.error("Error updating deal:", error);
    }
    
    // Update locally
    setDeals(deals.map(d => 
      d.deal_id === editingDeal.deal_id 
        ? { ...d, ...newDeal }
        : d
    ));
    
    setShowModal(false);
    setEditingDeal(null);
    setNewDeal({
      company_name: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      stage: "diagnostico",
      value: 0,
      description: "",
      active_routes: [],
    });
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm("¿Estás seguro de eliminar este deal?")) return;
    
    try {
      await axios.delete(`${API}/deals/${dealId}`, { withCredentials: true });
    } catch (error) {
      console.error("Error deleting deal:", error);
    }
    
    setDeals(deals.filter(d => d.deal_id !== dealId));
    setShowDealDetail(null);
  };

  const handleAddNote = async (dealId) => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(
        `${API}/deals/${dealId}/notes`,
        { content: newNote },
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error adding note:", error);
    }
    
    // Update local state
    const newNoteObj = { 
      note_id: `temp_${Date.now()}`, 
      content: newNote, 
      created_at: new Date().toISOString() 
    };
    
    setDeals(deals.map((d) => {
      if (d.deal_id === dealId) {
        return {
          ...d,
          notes: [...(d.notes || []), newNoteObj],
        };
      }
      return d;
    }));
    
    // Update detail view if open
    if (showDealDetail?.deal_id === dealId) {
      setShowDealDetail({
        ...showDealDetail,
        notes: [...(showDealDetail.notes || []), newNoteObj],
      });
    }
    
    setNewNote("");
  };

  const getDealsByStage = (stage) => {
    return deals.filter((d) => {
      const matchesStage = d.stage === stage;
      const matchesSearch = !searchQuery || 
        d.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStage && matchesSearch;
    });
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getTotalByStage = (stage) => {
    return getDealsByStage(stage).reduce((sum, d) => sum + (d.value || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wca-network">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <Briefcase size={24} className="text-white sm:w-7 sm:h-7" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">WCA | Network</h1>
            <p className="text-sm sm:text-base text-slate-500">CRM B2B - Pipeline de ventas y clientes</p>
          </div>
        </div>
        <button
          onClick={() => {
            setEditingDeal(null);
            setNewDeal({
              company_name: "",
              contact_name: "",
              contact_email: "",
              contact_phone: "",
              stage: "diagnostico",
              value: 0,
              description: "",
              active_routes: [],
            });
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center"
          data-testid="create-deal-btn"
        >
          <Plus size={20} />
          Nuevo Deal
        </button>
      </div>

      {/* Search */}
      <div className="glass-panel p-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Buscar deals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full glass-input pl-10"
            data-testid="search-deals-input"
          />
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stages.map((stage) => (
          <div key={stage.id} className="glass-panel p-4">
            <div className="flex items-center gap-3 mb-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: stage.color }}
              ></div>
              <span className="text-sm font-medium text-slate-600">{stage.label}</span>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-800">
              {formatCurrency(getTotalByStage(stage.id))}
            </p>
            <p className="text-sm text-slate-500">
              {getDealsByStage(stage.id).length} deals
            </p>
          </div>
        ))}
      </div>

      {/* Pipeline - Vertical Layout */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-6">
          {stages.map((stage) => (
            <div key={stage.id} className="glass-panel p-4 sm:p-6">
              {/* Stage Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: stage.color }}
                  ></div>
                  <h3 className="font-semibold text-slate-700">{stage.label}</h3>
                  <span className="text-sm text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    {getDealsByStage(stage.id).length}
                  </span>
                </div>
                <span className="text-lg font-bold text-primary">
                  {formatCurrency(getTotalByStage(stage.id))}
                </span>
              </div>

              {/* Deals */}
              <Droppable droppableId={stage.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-[100px] rounded-xl transition-colors ${
                      snapshot.isDraggingOver ? "bg-primary/5" : ""
                    }`}
                    data-testid={`pipeline-${stage.id}`}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {getDealsByStage(stage.id).map((deal, index) => (
                        <Draggable
                          key={deal.deal_id}
                          draggableId={deal.deal_id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-white rounded-2xl p-4 shadow-sm border border-slate-100 transition-all ${
                                snapshot.isDragging ? "shadow-xl rotate-1" : "hover:shadow-md"
                              }`}
                              data-testid={`deal-${deal.deal_id}`}
                            >
                              <div className="flex items-start gap-2 mb-3">
                                <div
                                  {...provided.dragHandleProps}
                                  className="p-1 hover:bg-slate-100 rounded cursor-grab active:cursor-grabbing flex-shrink-0"
                                >
                                  <GripVertical size={16} className="text-slate-400" />
                                </div>
                                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                  <Building2 size={16} className="text-slate-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-slate-800 text-sm truncate">
                                    {deal.company_name}
                                  </h4>
                                  <p className="text-xs text-slate-500 truncate">
                                    {deal.contact_name}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-lg font-bold text-primary">
                                  {formatCurrency(deal.value)}
                                </span>
                              </div>
                              
                              <div className="flex flex-wrap gap-1 mb-3">
                                {deal.active_routes?.slice(0, 2).map((route, i) => (
                                  <span
                                    key={i}
                                    className="badge bg-slate-100 text-slate-600 text-xs"
                                  >
                                    {route}
                                  </span>
                                ))}
                                {deal.active_routes?.length > 2 && (
                                  <span className="badge bg-slate-100 text-slate-600 text-xs">
                                    +{deal.active_routes.length - 2}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                <button
                                  onClick={() => setShowDealDetail(deal)}
                                  className="text-xs text-primary hover:underline"
                                >
                                  Ver detalles
                                </button>
                                {isSuperAdmin && (
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditDeal(deal);
                                      }}
                                      className="p-1.5 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                                      title="Editar"
                                    >
                                      <Edit size={14} />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteDeal(deal.deal_id);
                                      }}
                                      className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                                      title="Eliminar"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                    </div>
                    {provided.placeholder}
                    
                    {getDealsByStage(stage.id).length === 0 && (
                      <div className="text-center py-8 text-slate-400">
                        <p className="text-sm">Sin deals en esta etapa</p>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {/* Deal Detail Modal */}
      {showDealDetail && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-6 sm:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up" data-testid="deal-detail-modal">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                  <Building2 size={24} className="text-slate-600 sm:w-7 sm:h-7" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-slate-800">
                    {showDealDetail.company_name}
                  </h2>
                  <p className="text-slate-500">{showDealDetail.contact_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isSuperAdmin && (
                  <>
                    <button
                      onClick={() => handleEditDeal(showDealDetail)}
                      className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteDeal(showDealDetail.deal_id)}
                      className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowDealDetail(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50">
                  <Mail size={20} className="text-slate-400" />
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="text-slate-800 truncate">{showDealDetail.contact_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50">
                  <Phone size={20} className="text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Teléfono</p>
                    <p className="text-slate-800">{showDealDetail.contact_phone || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* Value & Stage */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-primary/5">
                  <p className="text-xs text-slate-500 mb-1">Valor del Deal</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(showDealDetail.value)}
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-slate-50">
                  <p className="text-xs text-slate-500 mb-1">Etapa Actual</p>
                  <span className="badge badge-primary text-lg px-4 py-1">
                    {stages.find((s) => s.id === showDealDetail.stage)?.label}
                  </span>
                </div>
              </div>

              {/* Description */}
              {showDealDetail.description && (
                <div>
                  <h3 className="font-semibold text-slate-700 mb-2">Descripción</h3>
                  <p className="text-slate-600">{showDealDetail.description}</p>
                </div>
              )}

              {/* Active Routes */}
              {showDealDetail.active_routes?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-700 mb-2">Rutas de Transformación</h3>
                  <div className="flex flex-wrap gap-2">
                    {showDealDetail.active_routes.map((route, i) => (
                      <span key={i} className="badge badge-primary">
                        {route}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-3">Notas</h3>
                <div className="space-y-3 mb-4">
                  {showDealDetail.notes?.length > 0 ? (
                    showDealDetail.notes.map((note) => (
                      <div
                        key={note.note_id}
                        className="p-4 rounded-2xl bg-slate-50"
                      >
                        <p className="text-slate-700">{note.content}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(note.created_at).toLocaleDateString("es-ES")}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-sm">No hay notas aún</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="flex-1 glass-input"
                    placeholder="Agregar una nota..."
                    data-testid="note-input"
                  />
                  <button
                    onClick={() => handleAddNote(showDealDetail.deal_id)}
                    className="btn-primary px-4"
                    data-testid="add-note-btn"
                  >
                    <MessageSquare size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Deal Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-6 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up" data-testid="create-deal-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">
                {editingDeal ? "Editar Deal" : "Nuevo Deal"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingDeal(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Empresa
                </label>
                <input
                  type="text"
                  value={newDeal.company_name}
                  onChange={(e) => setNewDeal({ ...newDeal, company_name: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Nombre de la empresa"
                  data-testid="deal-company-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Contacto
                  </label>
                  <input
                    type="text"
                    value={newDeal.contact_name}
                    onChange={(e) => setNewDeal({ ...newDeal, contact_name: e.target.value })}
                    className="w-full glass-input"
                    placeholder="Nombre del contacto"
                    data-testid="deal-contact-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newDeal.contact_email}
                    onChange={(e) => setNewDeal({ ...newDeal, contact_email: e.target.value })}
                    className="w-full glass-input"
                    placeholder="email@empresa.com"
                    data-testid="deal-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={newDeal.contact_phone}
                    onChange={(e) => setNewDeal({ ...newDeal, contact_phone: e.target.value })}
                    className="w-full glass-input"
                    placeholder="+52..."
                    data-testid="deal-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Valor (MXN)
                  </label>
                  <input
                    type="number"
                    value={newDeal.value}
                    onChange={(e) => setNewDeal({ ...newDeal, value: parseInt(e.target.value) || 0 })}
                    className="w-full glass-input"
                    placeholder="100000"
                    data-testid="deal-value-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Etapa
                </label>
                <select
                  value={newDeal.stage}
                  onChange={(e) => setNewDeal({ ...newDeal, stage: e.target.value })}
                  className="w-full glass-input"
                  data-testid="deal-stage-select"
                >
                  {stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>{stage.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={newDeal.description}
                  onChange={(e) => setNewDeal({ ...newDeal, description: e.target.value })}
                  className="w-full glass-input min-h-[80px]"
                  placeholder="Descripción del proyecto"
                  data-testid="deal-description-input"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                <button 
                  onClick={() => {
                    setShowModal(false);
                    setEditingDeal(null);
                  }} 
                  className="btn-secondary order-2 sm:order-1"
                >
                  Cancelar
                </button>
                <button 
                  onClick={editingDeal ? handleUpdateDeal : handleCreateDeal}
                  disabled={!newDeal.company_name.trim()}
                  className="btn-primary order-1 sm:order-2" 
                  data-testid="save-deal-btn"
                >
                  {editingDeal ? "Guardar Cambios" : "Crear Deal"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WCANetwork;
